// src/components/Frontend/PricingPlans.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getPricingPlans,
  createPricingPlan,
  updatePricingPlan,
  deletePricingPlan,
  togglePricingPlanStatus,
  exportPricingPlans,
  clearApiMessage,
  clearError,
  clearState,
  clearCurrentItem,
  selectPricingPlans,
  selectPricingPlanPagination,
  selectCurrentPricingPlan,
  selectPricingPlansLoading,
  selectPricingPlansSubmitting,
  selectPricingPlansApiMessage,
  selectPricingPlansError,
} from '../../store/slices/frontend/pricingPlanSlice';
import {
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  Plus,
  ToggleLeft,
  ToggleRight,
  Gem,
  LayoutGrid,
  List,
  ClipboardList,
  Save,
  Clock,
  Eye,
  Star,
} from 'lucide-react';

const PricingPlans = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Refs for add
  const nameRef = useRef(null);
  const priceRef = useRef(null);
  const currencyRef = useRef(null);
  const periodRef = useRef(null);
  const featuresRef = useRef(null);
  const descRef = useRef(null);
  const ctaTextRef = useRef(null);
  const ctaLinkRef = useRef(null);
  const isFeaturedRef = useRef(null);
  const isCustomRef = useRef(null);
  const iconRef = useRef(null);
  const colorRef = useRef(null);
  const orderRef = useRef(null);
  const activeRef = useRef(null);

  // Refs for edit
  const editNameRef = useRef(null);
  const editPriceRef = useRef(null);
  const editCurrencyRef = useRef(null);
  const editPeriodRef = useRef(null);
  const editFeaturesRef = useRef(null);
  const editDescRef = useRef(null);
  const editCtaTextRef = useRef(null);
  const editCtaLinkRef = useRef(null);
  const editIsFeaturedRef = useRef(null);
  const editIsCustomRef = useRef(null);
  const editIconRef = useRef(null);
  const editColorRef = useRef(null);
  const editOrderRef = useRef(null);
  const editActiveRef = useRef(null);

  const editingIdRef = useRef(null);
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  const items = useSelector(selectPricingPlans) || [];
  const pagination = useSelector(selectPricingPlanPagination) || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
  const loading = useSelector(selectPricingPlansLoading);
  const isSubmittingRedux = useSelector(selectPricingPlansSubmitting);
  const apiMessage = useSelector(selectPricingPlansApiMessage);
  const error = useSelector(selectPricingPlansError);

  useEffect(() => {
    dispatch(getPricingPlans({ per_page: itemsPerPage, page: currentPage }));
    return () => dispatch(clearState());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = { search: searchTerm, per_page: itemsPerPage, page: currentPage };
      if (statusFilter) params.is_active = statusFilter === 'active' ? true : false;
      dispatch(getPricingPlans(params));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    const msg = apiMessage || (error ? error : null);
    if (!msg || !showToast || !shouldShowApiToast(msg, !!error)) return;
    const messageKey = `${msg}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    if (lastToastRef.current.message === messageKey && (now - lastToastRef.current.timestamp) < 2000) return;
    lastToastRef.current = { message: messageKey, timestamp: now };
    showToast(msg, error ? 'error' : 'success');
    setTimeout(() => { dispatch(clearApiMessage()); dispatch(clearError()); }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  const showToastMessage = useCallback((msg, type = 'info') => {
    if (showToast && typeof showToast === 'function') showToast(msg, type);
  }, [showToast]);

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;
  const currentPageNum = pagination.current_page || 1;

  const goToPage = (page) => { if (page >= 1 && page <= totalPages) { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToPreviousPage = () => { if (currentPageNum > 1) { setCurrentPage(currentPageNum - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToNextPage = () => { if (currentPageNum < totalPages) { setCurrentPage(currentPageNum + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) { startPage = Math.max(1, endPage - maxPagesToShow + 1); }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return pageNumbers;
  };

  const getStatusBadge = (isActive) => isActive ? <span className="badge badge-green">Active</span> : <span className="badge badge-amber">Inactive</span>;
  const isActive = (item) => item.is_active === true;

  const validateForm = (values) => {
    const errors = {};
    if (!values.name) errors.name = 'Name is required';
    if (!values.price) errors.price = 'Price is required';
    return errors;
  };

  const getFormValues = () => ({
    name: nameRef.current?.value?.trim() || '',
    price: parseFloat(priceRef.current?.value) || 0,
    currency: currencyRef.current?.value?.trim() || '₵',
    period: periodRef.current?.value?.trim() || '/month',
    features: (featuresRef.current?.value || '').split(',').map(s => s.trim()).filter(Boolean),
    description: descRef.current?.value?.trim() || '',
    cta_text: ctaTextRef.current?.value?.trim() || 'Get Started',
    cta_link: ctaLinkRef.current?.value?.trim() || '/signup',
    is_featured: isFeaturedRef.current?.checked || false,
    is_custom: isCustomRef.current?.checked || false,
    icon: iconRef.current?.value?.trim() || 'Sparkles',
    color: colorRef.current?.value?.trim() || '#D4AF37',
    order: parseInt(orderRef.current?.value) || 0,
    is_active: activeRef.current?.checked || false,
  });

  const getEditFormValues = () => ({
    name: editNameRef.current?.value?.trim() || '',
    price: parseFloat(editPriceRef.current?.value) || 0,
    currency: editCurrencyRef.current?.value?.trim() || '₵',
    period: editPeriodRef.current?.value?.trim() || '/month',
    features: (editFeaturesRef.current?.value || '').split(',').map(s => s.trim()).filter(Boolean),
    description: editDescRef.current?.value?.trim() || '',
    cta_text: editCtaTextRef.current?.value?.trim() || 'Get Started',
    cta_link: editCtaLinkRef.current?.value?.trim() || '/signup',
    is_featured: editIsFeaturedRef.current?.checked || false,
    is_custom: editIsCustomRef.current?.checked || false,
    icon: editIconRef.current?.value?.trim() || 'Sparkles',
    color: editColorRef.current?.value?.trim() || '#D4AF37',
    order: parseInt(editOrderRef.current?.value) || 0,
    is_active: editActiveRef.current?.checked || false,
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ==================== MODALS ====================

  const openCreateModal = () => {
    editingIdRef.current = null;
    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = '';
      if (priceRef.current) priceRef.current.value = '';
      if (currencyRef.current) currencyRef.current.value = '₵';
      if (periodRef.current) periodRef.current.value = '/month';
      if (featuresRef.current) featuresRef.current.value = '';
      if (descRef.current) descRef.current.value = '';
      if (ctaTextRef.current) ctaTextRef.current.value = 'Get Started';
      if (ctaLinkRef.current) ctaLinkRef.current.value = '/signup';
      if (isFeaturedRef.current) isFeaturedRef.current.checked = false;
      if (isCustomRef.current) isCustomRef.current.checked = false;
      if (iconRef.current) iconRef.current.value = 'Sparkles';
      if (colorRef.current) colorRef.current.value = '#D4AF37';
      if (orderRef.current) orderRef.current.value = '0';
      if (activeRef.current) activeRef.current.checked = true;
    }, 0);
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Pricing Plan</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Name <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" placeholder="e.g. Growth" ref={nameRef} /></div>
          <div className="form-group"><label className="form-label">Price <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" type="number" min="0" step="0.01" placeholder="99" ref={priceRef} /></div>
          <div className="form-group"><label className="form-label">Currency</label><input className="form-input" placeholder="₵" ref={currencyRef} /></div>
          <div className="form-group"><label className="form-label">Period</label><input className="form-input" placeholder="/month" ref={periodRef} /></div>
          <div className="form-group"><label className="form-label">Features (comma separated)</label><input className="form-input" placeholder="Unlimited members, SMS, Support" ref={featuresRef} /></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" rows="2" ref={descRef} /></div>
          <div className="form-group"><label className="form-label">CTA Text</label><input className="form-input" placeholder="Get Started" ref={ctaTextRef} /></div>
          <div className="form-group"><label className="form-label">CTA Link</label><input className="form-input" placeholder="/signup" ref={ctaLinkRef} /></div>
          <div className="form-group"><label className="form-label">Icon Name</label><input className="form-input" placeholder="Sparkles" ref={iconRef} /></div>
          <div className="form-group"><label className="form-label">Color</label><input className="form-input" placeholder="#D4AF37" ref={colorRef} /></div>
          <div className="form-group"><label className="form-label">Order</label><input className="form-input" type="number" min="0" ref={orderRef} defaultValue="0" /></div>
          <div className="form-group" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={isFeaturedRef} /> Featured</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={isCustomRef} /> Custom</label>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={activeRef} defaultChecked={true} /><span>Active</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) { showToastMessage(Object.values(errors)[0], 'error'); return; }
            setIsSubmitting(true);
            try {
              const result = await dispatch(createPricingPlan(values));
              if (result.payload?.success) { closeModal(); await dispatch(getPricingPlans({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Pricing plan created successfully!', 'success'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to create plan', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to create plan', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Creating...' : 'Create Plan'}</button>
        </div>
      </div>
    );
  };

  const handleEditPlan = (item) => {
    editingIdRef.current = item.id;
    setTimeout(() => {
      if (editNameRef.current) editNameRef.current.value = item.name || '';
      if (editPriceRef.current) editPriceRef.current.value = item.price || '';
      if (editCurrencyRef.current) editCurrencyRef.current.value = item.currency || '₵';
      if (editPeriodRef.current) editPeriodRef.current.value = item.period || '/month';
      if (editFeaturesRef.current) editFeaturesRef.current.value = (item.features || []).join(', ');
      if (editDescRef.current) editDescRef.current.value = item.description || '';
      if (editCtaTextRef.current) editCtaTextRef.current.value = item.cta_text || 'Get Started';
      if (editCtaLinkRef.current) editCtaLinkRef.current.value = item.cta_link || '/signup';
      if (editIsFeaturedRef.current) editIsFeaturedRef.current.checked = !!item.is_featured;
      if (editIsCustomRef.current) editIsCustomRef.current.checked = !!item.is_custom;
      if (editIconRef.current) editIconRef.current.value = item.icon || 'Sparkles';
      if (editColorRef.current) editColorRef.current.value = item.color || '#D4AF37';
      if (editOrderRef.current) editOrderRef.current.value = item.order || 0;
      if (editActiveRef.current) editActiveRef.current.checked = item.is_active !== false;
    }, 0);
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Plan: {item.name}</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Name <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" ref={editNameRef} /></div>
          <div className="form-group"><label className="form-label">Price <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" type="number" min="0" step="0.01" ref={editPriceRef} /></div>
          <div className="form-group"><label className="form-label">Currency</label><input className="form-input" ref={editCurrencyRef} /></div>
          <div className="form-group"><label className="form-label">Period</label><input className="form-input" ref={editPeriodRef} /></div>
          <div className="form-group"><label className="form-label">Features (comma separated)</label><input className="form-input" ref={editFeaturesRef} /></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" rows="2" ref={editDescRef} /></div>
          <div className="form-group"><label className="form-label">CTA Text</label><input className="form-input" ref={editCtaTextRef} /></div>
          <div className="form-group"><label className="form-label">CTA Link</label><input className="form-input" ref={editCtaLinkRef} /></div>
          <div className="form-group"><label className="form-label">Icon Name</label><input className="form-input" ref={editIconRef} /></div>
          <div className="form-group"><label className="form-label">Color</label><input className="form-input" ref={editColorRef} /></div>
          <div className="form-group"><label className="form-label">Order</label><input className="form-input" type="number" min="0" ref={editOrderRef} /></div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={editIsFeaturedRef} /> Featured</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={editIsCustomRef} /> Custom</label>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={editActiveRef} /><span>Active</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getEditFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) { showToastMessage(Object.values(errors)[0], 'error'); return; }
            setIsSubmitting(true);
            try {
              const result = await dispatch(updatePricingPlan({ id: item.id, data: values }));
              if (result.payload?.success) { closeModal(); await dispatch(getPricingPlans({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Plan updated successfully!', 'success'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to update plan', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to update plan', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}</button>
        </div>
      </div>
    );
  };

  const handleDeletePlan = (item) => {
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Plan</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>Are you sure you want to delete plan <strong>{item.name}</strong>?</p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> This will permanently remove this plan.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger btn-sm" disabled={isSubmitting} onClick={async () => {
            setIsSubmitting(true);
            try {
              const result = await dispatch(deletePricingPlan(item.id));
              if (result.payload?.success) { closeModal(); await dispatch(getPricingPlans({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Plan deleted successfully.', 'error'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to delete plan', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to delete plan', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Deleting...' : 'Delete Plan'}</button>
        </div>
      </div>
    );
  };

  const handleToggleStatus = async (id) => {
    setIsSubmitting(true);
    try {
      await dispatch(togglePricingPlanStatus(id)).unwrap();
      await dispatch(getPricingPlans({ per_page: itemsPerPage, page: currentPage }));
      showToastMessage('Status toggled', 'success');
    } catch (err) { showToastMessage(err.message || 'Toggle failed', 'error'); }
    finally { setIsSubmitting(false); }
  };

  const handleExport = async () => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(exportPricingPlans({ search: searchTerm }));
      if (result.payload?.success) showToastMessage('Plans exported successfully', 'success');
      else showToastMessage(result.payload?.message || 'Export failed', 'error');
    } catch (err) { showToastMessage(err.message || 'Export failed', 'error'); }
    finally { setIsSubmitting(false); }
  };

  const handleViewDetails = (item) => {
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Eye size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Plan Details</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Name</span><span>{item.name}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Price</span><span>{item.currency}{item.price} {item.period}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Features</span><span>{(item.features || []).join(', ')}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Featured</span><span>{item.is_featured ? 'Yes' : 'No'}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span><span>{getStatusBadge(item.is_active)}</span></div>
            {item.created_at && <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Created</span><span>{formatDate(item.created_at)}</span></div>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => { closeModal(); setTimeout(() => handleEditPlan(item), 100); }}><Pencil size={16} /> Edit</button>
        </div>
      </div>
    );
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderGridView = () => {
    return (
      <div className="plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
        {items.map(item => (
          <div key={item.id} className="card" style={{ borderTop: `3px solid ${item.is_active ? 'var(--gold)' : 'var(--text-light)'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: item.color || 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Gem size={22} color={item.color || 'var(--navy)'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{item.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{item.currency}{item.price}{item.period}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {getStatusBadge(item.is_active)}
                <label className="toggle" title={isActive(item) ? 'Click to deactivate' : 'Click to activate'}>
                  <input type="checkbox" checked={isActive(item)} onChange={(e) => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
              <div style={{ flex: 1, textAlign: 'center' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Featured</div><div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{item.is_featured ? 'Yes' : 'No'}</div></div>
              <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Order</div><div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{item.order || 0}</div></div>
              <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Created</div><div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)', marginTop: '2px' }}>{formatDate(item.created_at)}</div></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)', flex: 1, marginRight: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {(item.features || []).slice(0, 3).join(' • ')}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleViewDetails(item)} disabled={loading} title="View Details"><Eye size={14} /></button>
                <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleEditPlan(item)} disabled={loading} title="Edit"><Pencil size={14} /></button>
                <button className="btn btn-sm btn-icon btn-delete" onClick={() => handleDeletePlan(item)} disabled={loading} title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Pricing Plans</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Price</th><th>Features</th><th>Status</th><th style={{ minWidth: '120px' }}>Actions</th></tr></thead>
            <tbody>
              {items.length > 0 ? items.map(item => (
                <tr key={item.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: item.color || 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Gem size={14} color={item.color || 'var(--navy)'} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{item.name}</div>
                        {item.is_featured && <span className="badge badge-gold" style={{ background: 'var(--gold)', color: 'var(--navy)', fontSize: '9px' }}>Featured</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{item.currency}{item.price}{item.period}</td>
                  <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{(item.features || []).slice(0, 3).join(', ')}{(item.features || []).length > 3 ? '...' : ''}</td>
                  <td>{getStatusBadge(item.is_active)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {isActive(item) ? <button className="btn btn-sm btn-icon btn-toggle" onClick={() => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} title="Deactivate"><ToggleRight size={14} /></button> : <button className="btn btn-sm btn-icon btn-toggle" onClick={() => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} title="Activate"><ToggleLeft size={14} /></button>}
                      <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleViewDetails(item)} disabled={loading} title="View"><Eye size={14} /></button>
                      <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleEditPlan(item)} disabled={loading} title="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-sm btn-icon btn-delete" onClick={() => handleDeletePlan(item)} disabled={loading} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5"><div className="empty-state"><div className="empty-icon"><Gem size={40} strokeWidth={1.5} /></div><div className="empty-title">No plans found</div><div className="empty-text">Create your first pricing plan to get started.</div></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> plans</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} disabled={loading}>
                  <option value={3}>3</option><option value={6}>6</option><option value={9}>9</option><option value={12}>12</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button className="btn btn-ghost btn-sm" onClick={goToPreviousPage} disabled={currentPageNum === 1 || loading} style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}>← Prev</button>
                {getPageNumbers().map(page => <button key={page} className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`} onClick={() => goToPage(page)} disabled={loading}>{page}</button>)}
                <button className="btn btn-ghost btn-sm" onClick={goToNextPage} disabled={currentPageNum === totalPages || loading} style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}>Next →</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== STATS ====================
  const total = items.length;
  const activeCount = items.filter(s => s.is_active).length;
  const inactiveCount = items.filter(s => !s.is_active).length;
  const featuredCount = items.filter(s => s.is_featured).length;

  // ==================== MAIN RENDER ====================

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div><h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}><Gem size={20} style={{ verticalAlign: '-4px' }} /> Pricing Plan Management</h2><p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>Manage pricing plans displayed on the homepage.</p></div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={openCreateModal} disabled={isSubmitting}>+ Add Plan</button>
          <button className="btn btn-ghost btn-sm" onClick={handleExport} disabled={isSubmitting || loading}><Save size={14} style={{ verticalAlign: '-2px' }} /> Export</button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold"><div className="stat-label">Total Plans</div><div className="stat-value">{pagination.total || total}</div><div className="stat-change up">↑ total</div><div className="stat-icon"><Gem size={20} /></div></div>
        <div className="stat-card green"><div className="stat-label">Active</div><div className="stat-value">{activeCount}</div><div className="stat-change up">↑ active</div><div className="stat-icon"><CheckCircle2 size={20} /></div></div>
        <div className="stat-card amber"><div className="stat-label">Inactive</div><div className="stat-value">{inactiveCount}</div><div className="stat-change down">↓ inactive</div><div className="stat-icon"><XCircle size={20} /></div></div>
        <div className="stat-card blue"><div className="stat-label">Featured</div><div className="stat-value">{featuredCount}</div><div className="stat-change up">↑ featured</div><div className="stat-icon"><Star size={20} /></div></div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}><span className="search-icon"><Search size={14} /></span><input type="text" placeholder="Search plans..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
          <select className="form-select" style={{ width: '140px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setViewMode('grid')}><LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid</button>
            <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setViewMode('list')}><List size={14} style={{ verticalAlign: '-2px' }} /> List</button>
          </div>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} /><div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading plans...</div></div>
      ) : (
        items.length > 0 ? (viewMode === 'grid' ? renderGridView() : renderListView()) :
        <div className="empty-state"><div className="empty-icon"><Gem size={40} strokeWidth={1.5} /></div><div className="empty-title">No plans found</div><div className="empty-text">Create your first pricing plan to get started.</div></div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .stat-card { position: relative; padding: 16px 20px; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow); transition: transform 0.2s, box-shadow 0.2s; cursor: default; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
        .stat-card .stat-label { font-size: 12px; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
        .stat-card .stat-value { font-size: 28px; font-weight: 700; font-family: 'Playfair Display', serif; color: var(--navy); margin: 4px 0 2px; }
        .stat-card .stat-change { font-size: 12px; font-weight: 600; }
        .stat-card .stat-change.up { color: var(--green); }
        .stat-card .stat-change.down { color: var(--red); }
        .stat-card .stat-icon { position: absolute; top: 16px; right: 16px; opacity: 0.2; color: var(--navy); }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .stat-card.blue .stat-icon { color: var(--blue); }
        .badge { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 500; display: inline-block; }
        .badge-green { background: var(--green-bg); color: var(--green); }
        .badge-amber { background: var(--amber-bg); color: var(--amber); }
        .badge-blue { background: var(--blue-bg); color: var(--blue); }
        .badge-gold { background: var(--gold); color: var(--navy); }
        .toggle { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
        .toggle input { opacity: 0; width: 0; height: 0; }
        .toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #ccc; transition: 0.3s; border-radius: 22px; }
        .toggle-slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background: white; transition: 0.3s; border-radius: 50%; }
        .toggle input:checked + .toggle-slider { background: var(--gold); }
        .toggle input:checked + .toggle-slider:before { transform: translateX(18px); }
        .btn-edit { background: var(--blue-bg) !important; color: var(--blue) !important; border: 1px solid #93c5fd !important; }
        .btn-edit:hover { background: var(--blue) !important; color: white !important; border-color: var(--blue) !important; }
        .btn-delete { background: var(--red-bg) !important; color: var(--red) !important; border: 1px solid #fca5a5 !important; }
        .btn-delete:hover { background: var(--red) !important; color: white !important; border-color: var(--red) !important; }
        .btn-toggle { background: var(--cream) !important; color: var(--text-mid) !important; border: 1px solid var(--border) !important; }
        .btn-toggle:hover { background: var(--border) !important; color: var(--text) !important; }
        .btn-sm { padding: 4px 10px !important; font-size: 12px !important; }
        .btn-icon { padding: 4px 6px !important; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-gold { background: var(--gold) !important; color: var(--navy) !important; border: none !important; }
        .btn-gold:hover { background: var(--gold-dark) !important; color: var(--navy) !important; }
        .btn-danger { background: var(--red) !important; color: white !important; border: none !important; }
        .btn-danger:hover { background: var(--red-dark) !important; color: white !important; }
        .btn-ghost { background: transparent !important; color: var(--text) !important; border: 1px solid var(--border) !important; }
        .btn-ghost:hover { background: var(--cream) !important; color: var(--text) !important; }
        .btn-primary { background: var(--gold) !important; color: var(--navy) !important; border: none !important; }
        .btn-primary:hover { background: var(--gold-dark) !important; color: var(--navy) !important; }
        .empty-state { text-align: center; padding: 40px 0; }
        .empty-icon { font-size: 32px; margin-bottom: 12px; color: var(--text-light); }
        .empty-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
        .empty-text { font-size: 14px; color: var(--text-light); }
        .table-wrap { overflow-x: auto; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 10px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-light); border-bottom: 2px solid var(--border); background: var(--cream); }
        td { padding: 10px 12px; border-bottom: 1px solid var(--border-light); font-size: 13px; }
        tr:hover td { background: var(--cream); }
        .card { background: var(--white); border-radius: var(--radius); box-shadow: var(--shadow); padding: 16px; transition: transform 0.2s, box-shadow 0.2s; }
        .card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .search-box { display: flex; align-items: center; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 0 10px; transition: border-color 0.2s; height: 36px; box-sizing: border-box; }
        .search-box:focus-within { border-color: var(--gold); }
        .search-box input { border: none; padding: 8px 0; font-size: 13px; outline: none; width: 100%; background: transparent; color: var(--text); height: 100%; font-family: inherit; margin: 0; line-height: normal; box-sizing: border-box; }
        .search-box .search-icon { display: flex; align-items: center; justify-content: center; color: var(--text-light); margin-right: 6px; flex-shrink: 0; }
        @media (max-width: 1024px) { .stats-grid { grid-template-columns: repeat(2, 1fr) !important; } .plans-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: 1fr !important; } .plans-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
};

export default PricingPlans;